"use client";

import { useState, useEffect } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { UserProfile } from "../../data/profile/types";
import { Edit, Save, X, MapPin } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  profileUpdateSchema,
  ProfileUpdateInput,
} from "../../data/profile/schemas";
import { updateUserProfile } from "../../lib/profile/api";
import toast from "react-hot-toast";
import { KOREAN_REGIONS_DETAILED, parseRegion, formatRegion } from "../../lib/utils/regions";

interface ProfileAboutProps {
  user: UserProfile;
  isOwnProfile: boolean;
  onUpdate?: (updatedUser: UserProfile) => void;
  onEditNickname?: () => void;
}

export function ProfileAbout({
  user,
  isOwnProfile,
  onUpdate,
  onEditNickname,
}: ProfileAboutProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditingRegion, setIsEditingRegion] = useState(false);
  const [regionLoading, setRegionLoading] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ProfileUpdateInput>({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: {
      nickname: user.nickname,
      region: user.region,
      introShort: user.introShort || "",
      introLong: user.introLong || "",
    },
  });

  const watchedIntroShort = watch("introShort");
  const watchedIntroLong = watch("introLong");

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = async (data: ProfileUpdateInput) => {
    setLoading(true);
    try {
      // 자기소개만 업데이트
      const updateData = {
        introShort: data.introShort,
        introLong: data.introLong,
      };

      const result = await updateUserProfile(user.uid, updateData);

      if (result.success) {
        toast.success("자기소개가 업데이트되었습니다.");
        setIsEditing(false);

        // 부모 컴포넌트에 업데이트된 정보 전달
        if (onUpdate) {
          onUpdate({
            ...user,
            ...updateData,
          } as UserProfile);
        }
      } else {
        toast.error(result.error || "자기소개 업데이트에 실패했습니다.");
      }
    } catch (error) {
      console.error("자기소개 업데이트 실패:", error);
      toast.error("자기소개 업데이트 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 기존 지역 파싱
  useEffect(() => {
    if (user.region && isEditingRegion) {
      const parsed = parseRegion(user.region);
      setSelectedProvince(parsed.province || "");
      setSelectedCity(parsed.city || "");
    }
  }, [user.region, isEditingRegion]);

  const handleRegionUpdate = async () => {
    if (!selectedProvince) {
      toast.error("시/도를 선택해주세요.");
      return;
    }

    setRegionLoading(true);
    try {
      const regionValue = selectedCity 
        ? formatRegion(selectedProvince, selectedCity)
        : selectedProvince;

      const result = await updateUserProfile(user.uid, {
        region: regionValue,
      });

      if (result.success) {
        // toast.success("거래지역이 업데이트되었습니다.");
        setIsEditingRegion(false);
        setSelectedProvince("");
        setSelectedCity("");

        // 부모 컴포넌트에 업데이트된 정보 전달
        if (onUpdate) {
          onUpdate({
            ...user,
            region: regionValue,
          } as UserProfile);
        }
      } else {
        toast.error(result.error || "거래지역 업데이트에 실패했습니다.");
      }
    } catch (error) {
      console.error("거래지역 업데이트 실패:", error);
      toast.error("거래지역 업데이트 중 오류가 발생했습니다.");
    } finally {
      setRegionLoading(false);
    }
  };

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-semibold text-gray-900">프로필 정보</h2>
          {isOwnProfile && !isEditing && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleEdit}
              className="flex items-center gap-1.5"
            >
              <Edit className="w-4 h-4" />
              <span>수정</span>
            </Button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* 한 줄 소개 */}
            <div>
              <label className="block text-[13px] font-medium text-[#666] mb-1.5">
                한 줄 소개
                <span className="text-gray-500 text-xs ml-1">
                  ({watchedIntroShort?.length || 0}/50)
                </span>
              </label>
              <input
                {...register("introShort")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6C4EF3] text-[15px]"
                placeholder="간단한 자기소개를 입력하세요"
                maxLength={50}
              />
              {errors.introShort && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.introShort.message}
                </p>
              )}
            </div>

            {/* 자세한 자기소개 */}
            <div>
              <label className="block text-[13px] font-medium text-[#666] mb-1.5">
                자세한 자기소개
                <span className="text-gray-500 text-xs ml-1">
                  ({watchedIntroLong?.length || 0}/500)
                </span>
              </label>
              <textarea
                {...register("introLong")}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6C4EF3] text-[15px]"
                placeholder="자세한 자기소개를 입력하세요"
                maxLength={500}
              />
              {errors.introLong && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.introLong.message}
                </p>
              )}
            </div>

            {/* 버튼들 */}
            <div className="flex space-x-2 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex items-center bg-[#6C4EF3] hover:bg-[#5A3DD9] text-white"
              >
                {loading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {loading ? "저장 중..." : "저장"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center"
              >
                <X className="w-4 h-4 mr-2" />
                취소
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            {/* 닉네임 */}
            <div>
              <h3 className="text-[13px] font-medium text-[#666] mb-1.5">
                닉네임
              </h3>
              <div className="flex items-center justify-between">
                <p className="text-[15px] text-[#333]">{user.nickname}</p>
                {isOwnProfile && onEditNickname && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onEditNickname}
                    className="text-[#6C4EF3] hover:text-[#5A3DD9] hover:bg-[#6C4EF3]/5 p-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* 거래 지역 */}
            <div>
              <h3 className="text-[13px] font-medium text-[#666] mb-1.5 flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                거래 지역
              </h3>
              {isEditingRegion ? (
                <div className="space-y-3">
                  <select
                    value={selectedProvince}
                    onChange={e => {
                      setSelectedProvince(e.target.value);
                      setSelectedCity("");
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6C4EF3] text-[15px]"
                    disabled={regionLoading}
                  >
                    <option value="">시/도를 선택하세요</option>
                    {KOREAN_REGIONS_DETAILED.map(region => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </select>
                  {selectedProvince && 
                    KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)?.cities && (
                    <select
                      value={selectedCity}
                      onChange={e => setSelectedCity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#6C4EF3] text-[15px]"
                      disabled={regionLoading}
                    >
                      <option value="">시/군/구를 선택하세요 (선택사항)</option>
                      {KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince)?.cities?.map(city => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditingRegion(false);
                        setSelectedProvince("");
                        setSelectedCity("");
                      }}
                      disabled={regionLoading}
                    >
                      취소
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleRegionUpdate}
                      disabled={regionLoading || !selectedProvince}
                    >
                      저장
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-[15px] text-[#333]">
                    {user.region ? user.region.replace(/\s*>\s*/g, " ") : "지역 정보 없음"}
                  </p>
                  {isOwnProfile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingRegion(!isEditingRegion);
                        if (user.region) {
                          const parsed = parseRegion(user.region);
                          setSelectedProvince(parsed.province || "");
                          setSelectedCity(parsed.city || "");
                        }
                      }}
                      className="text-[#6C4EF3] hover:text-[#5A3DD9] hover:bg-[#6C4EF3]/5 p-1"
                      disabled={regionLoading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* 한 줄 소개 */}
            {user.introShort && (
              <div>
                <h3 className="text-[13px] font-medium text-[#666] mb-1.5">
                  한 줄 소개
                </h3>
                <p className="text-[15px] text-[#333]">{user.introShort}</p>
              </div>
            )}

            {/* 자세한 자기소개 */}
            {user.introLong && (
              <div>
                <h3 className="text-[13px] font-medium text-[#666] mb-1.5">
                  자기소개
                </h3>
                <p className="text-[15px] text-[#333] whitespace-pre-wrap">
                  {user.introLong}
                </p>
              </div>
            )}

            {/* 빈 상태 */}
            {!user.introShort && !user.introLong && (
              <div className="text-center py-8 text-gray-500">
                {isOwnProfile ? (
                  <div>
                    <p className="mb-2">아직 자기소개가 없습니다.</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEdit}
                      className="border-[#6C4EF3] text-[#6C4EF3] hover:bg-[#6C4EF3]/5"
                    >
                      자기소개 작성하기
                    </Button>
                  </div>
                ) : (
                  <p>자기소개가 없습니다.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
